import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import {
  ContinueLearningCard,
  PlaceholderContinueLearningCard,
  CourseCard, 
  PlaceholderCourseCard, 
  PodcastCard, 
  SimulationCard
} from "./card";
import Text from "src/components/text";
import PaginationIndicator from "../components/pagination";

import { ScrollableContainer } from "src/components/scroller";
import InfiniteScroll from "src/components/infiniteScroll";
import { Skill, UpdateSkillPreference } from "src/types/coaching/courses";
import { fetchMyCourses } from "src/redux/actions/coaching/learners";
import { fetchPreferencesSkills, fetchPublicCourses } from "src/redux/actions/coaching/explore";
import { fetchTeamsMostEnrolledLearningsReport } from "src/redux/actions/coaching/reports";
import { fetchCallMeetings } from "src/redux/actions/coaching/callLibrary";

import {
  fetchSkills,
  invalidateSkills,
  updateSkillsPreference,
} from "src/redux/actions/coaching/settings";

import { CallMeeting } from "src/types/coaching/callLibrary";

import _ from "lodash";
import cx from "classnames";
import styles from "./explore.module.scss";
import { loading } from "src/localization/english/commons";
import {
  COACHING_EXPLORE_PATH,
  buildOriginNavigationState,
} from "src/pages/coaching/utils/manageNavigation";

const EmptyResult = '/images/assets/assetsEmptyMessage.svg'

const ExploreCoaching: React.FC<object> = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => _.get(state, "users.me"));

  useEffect(() => {
    dispatch(fetchPublicCourses({courseType: "ALL", page: 0, limit: 10}));
    dispatch(fetchPublicCourses({courseType: "SIMULATION", page: 0, limit: 10}));
    dispatch(fetchPublicCourses({courseType: "PODCAST", page: 0, limit: 10}));
    dispatch(fetchPublicCourses({courseType: "COURSE", page: 0, limit: 10}));
    dispatch(fetchMyCourses({ status: "IN_PROGRESS", page: 0, limit: 10 }));
    dispatch(fetchSkills({
      query: "",
      parent: null,
      page: 0,
      limit: 20,
    }));
    dispatch(fetchPreferencesSkills());
    dispatch(fetchTeamsMostEnrolledLearningsReport({}));

    return () => {
      dispatch(invalidateSkills());
    };
  }, []);

  const { loading, totalLearningsCount } = useSelector(
    (state: any) => {
      let courseType = "all"
      const learningsState = _.get(state.coaching.explore.public, courseType);
      const loading = _.get(learningsState, "loading");
      const pagination = _.get(learningsState, "pagination");
      const totalRecordsCount = _.get(pagination, "totalRecordsCount")

      return {
        loading,
        totalLearningsCount :totalRecordsCount
      };
    },
    _.isEqual,
  );


  const { totalCallCount, callLoading } = useSelector((state: any) => {
    const callMeetingState = _.get(state.coaching, "callMeetings", {});
    const loading = _.get(callMeetingState, "loading", false);
    const pagination = _.get(callMeetingState, "pagination");
    const totalCallCount = _.get(pagination, "totalRecordsCount", 0)

    return { totalCallCount, callLoading: loading  };
  }, _.isEqual);


  if (totalLearningsCount === 0 && totalCallCount === 0 && !loading && !callLoading) {
    return <NoLearningPlaceholder />;
  }

  return (
    <section className={styles.container}>

      <CallCard />
      <ContinueLearnings />
      <PopularLearnings />
      <PublicCourses type="SIMULATION" />
      <PublicCourses type="COURSE" />
      <PublicCourses type="PODCAST" />
      <PublicCourses type="ALL" />
      <PersonalisedSkills />
    </section>
  );
};

export { ExploreCoaching };

interface PublicCoursesProps {
  type: string;
}

const PublicCourses: React.FC<PublicCoursesProps> = ({type}) => {
    const [activeItemIndex, setActiveItemIndex] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const { loading, learnings, hasNext, nextPage, total, currentPage, totalRecordsCount } = useSelector(
    (state: any) => {
      let courseType = "all"
      if (type === "COURSE") {
        courseType = "courses";
      }
      
      if (type === "SIMULATION") {
        courseType = "simulations";
      } 
      
      if (type === "PODCAST") {
        courseType = "podcasts";
      }
      const learningsState = _.get(state.coaching.explore.public, courseType);
      const loading = _.get(learningsState, "loading");
      const learnings = _.get(learningsState, "items") || [];
      const pagination = _.get(learningsState, "pagination");
      const currentPage = _.get(pagination, "currentPage", 0);
      const total = _.get(pagination, "totalPages", 0);
      const totalRecordsCount = _.get(pagination, "totalRecordsCount")
      const hasNext = _.get(pagination, "hasMoreItems", false);

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        learnings,
        loading,
        hasNext,
        nextPage,
        total,
        currentPage,
        totalRecordsCount
      };
    },
    _.isEqual,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const PAGE_LIMIT = 10;
  const pendingScrollItemIndexRef = useRef<number | null>(null);

  const fetchLearningsForPage = useCallback((page: number) => {
    dispatch(
      fetchPublicCourses({
        courseType: type,
        page,
        limit: PAGE_LIMIT,
      }),
    );
  }, [type]);

  const handlePaginationClick = useCallback((itemIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const items = container.querySelectorAll<HTMLElement>(
      "[data-carousel-pagination-item='true']"
    );

    const targetItem = items[itemIndex];

  if (targetItem) {
    container.scrollTo({
      left: targetItem.offsetLeft,
      behavior: "smooth",
    });
    return;
  }

  pendingScrollItemIndexRef.current = itemIndex;
  const requiredPage = Math.floor(itemIndex / PAGE_LIMIT);
  if (requiredPage > currentPage) {
    fetchLearningsForPage(requiredPage);
  }
  }, [currentPage, fetchLearningsForPage]);

  const syncActiveItemIndex = useCallback((container: HTMLDivElement) => {

    if (!scrollContainerRef.current) {
      scrollContainerRef.current = container;
    }

    const itemElements = Array.from(
      container.querySelectorAll<HTMLElement>("[data-carousel-pagination-item='true']"),
    );

    if (_.isEmpty(itemElements)) {
      setActiveItemIndex(0);
      return;
    }

    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const isAtEnd = Math.abs(scrollLeft - maxScroll) < 2;

    if (isAtEnd) {
      setActiveItemIndex(itemElements.length - 1);
      return;
    }

    const nextActiveIndex = itemElements.findIndex(
      (item) => item.offsetLeft + item.offsetWidth / 2 > scrollLeft + 1,
    );

    setActiveItemIndex(
      nextActiveIndex === -1 ? itemElements.length - 1 : nextActiveIndex,
    );
  }, []);


  const handleLoadMoreLearnings = () => {
    if (nextPage) {
      fetchLearningsForPage(nextPage);
    }
  };

  const handleClick = (item) => {
    const navigationState = {
      state: buildOriginNavigationState(COACHING_EXPLORE_PATH),
    };

    if (item.entityType === "course") {
      navigate(`/coaching/explore/courses/${item.id}`, navigationState);
    } else if (item.entityType === "simulation") {
      navigate(`/coaching/explore/simulations/${item.id}`, navigationState);
    } else if (item.entityType === "podcast") {
      navigate(`/coaching/explore/podcasts/${item.id}`, navigationState);
    }
  };

  useEffect(() => {
    const pendingIndex = pendingScrollItemIndexRef.current;
    const container = scrollContainerRef.current;

    if (pendingIndex === null || !container) return;

    const items = container.querySelectorAll<HTMLElement>(
      "[data-carousel-pagination-item='true']"
    );

    const targetItem = items[pendingIndex];

    if (!targetItem) return;

    container.scrollTo({
      left: targetItem.offsetLeft,
      behavior: "smooth",
    });

    pendingScrollItemIndexRef.current = null;
  }, [learnings]);

  const getHeading = (heading:String) => {
    if (loading) {
      return <span className={styles.cardHeading} />;
    }
  
    return (
      <span className={styles.text}>
        {heading}
      </span>
    );
  };

  const getHeader = (type: string) => {

    if (type === "SIMULATION") {
      return <header className={styles.title}>
        <div>
        {getHeading("simulations")}

        {/* <span className={styles.action}>
          <span className={styles.seeAll}>see all</span>
          <i className="paperflite-up-arrow" />
        </span> */}
        </div>
        <PaginationIndicator
          total={total}
          current={currentPage}
          totalItems={totalRecordsCount}
          activeItemIndex={activeItemIndex}
          groupSize={5}
        />
      </header>;
    }

    if (type === "PODCAST") {
      return <header className={styles.title}>
        <div>
          {getHeading("podcasts")}

          {/* <span className={styles.action}>
            <span className={styles.seeAll}>see all</span>
            <i className="paperflite-up-arrow" />
          </span> */}
        </div>
        <PaginationIndicator
          total={total}
          current={currentPage}
          totalItems={totalRecordsCount}
          activeItemIndex={activeItemIndex}
          groupSize={5}
        />
      </header>;
    }

    if (type === "COURSE") {
      return <header className={styles.title}>
        <div>
           {getHeading("courses")}

          {/* <span className={styles.action}>
            <span className={styles.seeAll}>see all</span>
            <i className="paperflite-up-arrow" />
          </span> */}
        </div>
        <PaginationIndicator
          total={total}
          current={currentPage}
          totalItems={totalRecordsCount}
          activeItemIndex={activeItemIndex}
          groupSize={5}
        />
      </header>
    }

    if (type === "ALL") {
      return <header className={styles.title}>
        <div>
          <span className={styles.text}>
            recommended for the skills you&rsquo;re building
          </span>

          {/* <span className={styles.action}>
            <span className={styles.seeAll}>see all</span>
            <i className="paperflite-up-arrow" />
          </span> */}
        </div>
        <PaginationIndicator
          total={total}
          current={currentPage}
          totalItems={totalRecordsCount}
          activeItemIndex={activeItemIndex}
          groupSize={5}
        />
      </header>
    }
  };

  useEffect(() => {
    setActiveItemIndex((previous) => {
      if (_.isEmpty(learnings)) {
        return 0;
      }

      return Math.min(previous, learnings.length - 1);
    });
  }, [learnings]);

  if (learnings.length === 0 && !loading) {
    return null;
  }

  return (
    <section className={cx(styles.sectionContainer, styles.popularLearnings)}>
      {getHeader(type)}
      <div className={styles.learnings}>
        <ScrollableContainer
          distance={300}
          wrapperClassName={styles.courseList}
          leftArrowComponent={LeftArrow}
          rightArrowComponent={RightArrow}
          onScrollPositionChange={syncActiveItemIndex}
        >
          <InfiniteScroll
            loading={loading}
            className={styles.list}
            hasMore={hasNext}
            loadMore={handleLoadMoreLearnings}
            loader={() => {
              return _.times(_.size(learnings) === 0 ? 4 : 1).map((i) => (
                <PlaceholderCourseCard key={i} />
              ));
            }}
          >
            {learnings.map((item, index) => (
              <div key={index} className={styles.wrapper} data-carousel-pagination-item="true">
                {item.entityType === "course" && (
                  <CourseCard
                    key={item.id}
                    course={item.course}
                    onClick={() => handleClick(item)}
                  />
                )}

                {item.entityType === "simulation" && (
                  <SimulationCard
                    key={item.id}
                    course={item.course}
                    onClick={() => handleClick(item)}
                  />
                )}

                {item.entityType === "podcast" && (
                  <PodcastCard
                    key={item.id}
                    course={item.course}
                    onClick={() => handleClick(item)}
                  />
                )}
              </div>
            ))}
          </InfiniteScroll>
        </ScrollableContainer>
      </div>
    </section>
  );
};

interface PersonalisedSkillsProps {}

const PersonalisedSkills: React.FC<PersonalisedSkillsProps> = () => {
  const dispatch = useDispatch();

  const skills = useSelector((state: any) =>
    _.get(state, "coaching.settings.skills.items", []),
  );

  const selectedPreferenceSkills = useSelector((state: any) =>
    _.get(state.coaching, "explore.preferences.selectedSkills", []),
  );

  const handleClick = (skill: Skill) => {
    const selected = _.some(selectedPreferenceSkills, { id: skill.id });

    let updatedSkills;

    if (selected) {
      updatedSkills = selectedPreferenceSkills.map(
        (s: UpdateSkillPreference) =>
          s.id === skill.id ? { ...s, selected: false } : s,
      );
    } else {
      updatedSkills = [
        ...selectedPreferenceSkills,
        { id: skill.id, selected: true },
      ];
    }

    const request = {
      skills: updatedSkills,
    };

    dispatch(updateSkillsPreference(request));
  };

  if(loading && skills.length === 0) {
    return (
      <div></div>
    )
  }

  if(skills.length === 0){
    return null
  }

  return (
    <section
      className={cx(styles.sectionContainer, styles.predefinedLearnings)}
    >
      <div className={styles.skills}>
        <h3 className={styles.title}>personalised topics for you</h3>

        <div className={styles.items}>
          {skills.map((skill: Skill) => {
            const selected = _.some(selectedPreferenceSkills, { id: skill.id });
            return (
              <div
                key={skill.id}
                className={cx(styles.item, {
                  [styles.selected]: selected,
                })}
                onClick={() => handleClick(skill)}
              >
                {selected && <i className="paperflite-tick" />}
                {skill.name}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

interface ContinueLearningsProps {}

const ContinueLearnings: React.FC<ContinueLearningsProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const { learnings, loading, hasNext, nextPage, total, currentPage, totalRecordsCount } = useSelector(
    (state) => {
      const learningState = _.get(state, `learners.courses.inProgress`);

      const learnings = _.get(learningState, "items") || [];
      const loading = _.get(learningState, "loading");
      const pagination = _.get(learningState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasNext = _.get(pagination, "hasMoreItems", false);
      const currentPage = _.get(pagination, "currentPage", 0);
      const totalRecordsCount = _.get(pagination, "totalRecordsCount")

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        learnings,
        loading,
        hasNext,
        nextPage,
        total,
        currentPage,
        totalRecordsCount
      };
    },
    _.isEqual,
  );


  const handleLoadMoreLearnings = () => {
    if (nextPage) {
      fetchLearningsForPage(nextPage);
    }
  };

  const handleClick = (record) => {
    const entityType = _.get(record, "course.entityType");
    const navigationState = {
      state: buildOriginNavigationState(COACHING_EXPLORE_PATH),
    };

    if (entityType === "course") {
      navigate(`/coaching/courses/${record.id}`, navigationState);
    } else if (entityType === "simulation") {
      navigate(`/coaching/simulations/${record.id}`, navigationState);
    } else if (entityType === "podcast") {
      navigate(`/coaching/podcasts/${record.id}`, navigationState);
    }
  };

  const PAGE_LIMIT = 10;
  const pendingScrollItemIndexRef = useRef<number | null>(null);

  const fetchLearningsForPage = useCallback((page: number) => {
    dispatch(fetchMyCourses({ status: "IN_PROGRESS", page, limit: PAGE_LIMIT }));
  }, []);

  const handlePaginationClick = useCallback((itemIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const items = container.querySelectorAll<HTMLElement>(
      "[data-carousel-pagination-item='true']"
    );

    const targetItem = items[itemIndex];

    if (targetItem) {
      container.scrollTo({
        left: targetItem.offsetLeft,
        behavior: "smooth",
      });
      return;
    }

    pendingScrollItemIndexRef.current = itemIndex;

    const requiredPage = Math.floor(itemIndex / PAGE_LIMIT);

    if (requiredPage > currentPage) {
      fetchLearningsForPage(requiredPage);
    }
  }, [currentPage, fetchLearningsForPage]);

  const syncActiveItemIndex = useCallback((container: HTMLDivElement) => {

    if (!scrollContainerRef.current) {
      scrollContainerRef.current = container;
    }

    const itemElements = Array.from(
      container.querySelectorAll<HTMLElement>("[data-carousel-pagination-item='true']"),
    );

    if (_.isEmpty(itemElements)) {
      setActiveItemIndex(0);
      return;
    }

    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const isAtEnd = Math.abs(scrollLeft - maxScroll) < 2;

    if (isAtEnd) {
      setActiveItemIndex(itemElements.length - 1);
      return;
    }

    const nextActiveIndex = itemElements.findIndex(
      (item) => item.offsetLeft + item.offsetWidth / 2 > scrollLeft + 1,
    );

    setActiveItemIndex(
      nextActiveIndex === -1 ? itemElements.length - 1 : nextActiveIndex,
    );
  }, []);

  useEffect(() => {
    setActiveItemIndex((previous) => {
      if (_.isEmpty(learnings)) {
        return 0;
      }

      return Math.min(previous, learnings.length - 1);
    });
  }, [learnings]);

  useEffect(() => {
    const pendingIndex = pendingScrollItemIndexRef.current;
    const container = scrollContainerRef.current;

    if (pendingIndex === null || !container) return;

    const itemElements = container.querySelectorAll<HTMLElement>(
      "[data-carousel-pagination-item='true']",
    );

    const targetItem = itemElements[pendingIndex];

    if (!targetItem) return;

    container.scrollTo({
      left: targetItem.offsetLeft,
      behavior: "smooth",
    });

    pendingScrollItemIndexRef.current = null;
  }, [learnings]);

  if (learnings.length === 0 && !loading) {
    return null;
  }

  const getHeader = () => {
    if (loading) {
      return <span className={styles.cardHeading} />;
    }
  
    return (
      <span className={styles.text}>
        pick up where you left off
      </span>
    );
  };

  return (
    <section className={cx(styles.sectionContainer, styles.continueLearnings)}>
      <header className={styles.title}>
        <div>
          {getHeader()}
          {/* <span className={styles.action}>
            <span className={styles.seeAll}>see all</span>
            <i className="paperflite-up-arrow" />
          </span> */}
        </div>
        <PaginationIndicator
          total={total}
          current={currentPage}
          totalItems={totalRecordsCount}
          activeItemIndex={activeItemIndex}
          groupSize={5}
        />
      </header>
      <div className={styles.learnings}>
        <ScrollableContainer
          wrapperClassName={styles.continueLearningList}
          distance={300}
          leftArrowComponent={LeftArrow}
          rightArrowComponent={RightArrow}
          onScrollPositionChange={syncActiveItemIndex}
        >
          <InfiniteScroll
            className={styles.list}
            loading={loading}
            hasMore={hasNext}
            loadMore={handleLoadMoreLearnings}
            loader={() => {
              return _.times(_.size(learnings) === 0 ? 4 : 1).map((i) => (
                <PlaceholderContinueLearningCard key={i} />
              ));
            }}
          >
            {learnings.map((item: any) => {
              return (
                <div data-carousel-pagination-item="true">
                  <ContinueLearningCard
                    key={item.id}
                    course={item}
                    onClick={() => handleClick(item)}
                  />
                </div>
              );
            })}
          </InfiniteScroll>
        </ScrollableContainer>
      </div>
    </section>
  );
};

interface PopularLearningsProps {}

const PopularLearnings: React.FC<PopularLearningsProps> = () => {
  const navigate = useNavigate();

  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const { learnings, loading } = useSelector((state) => {
    const reportsState = _.get(
      state,
      "coaching.reports.teams.mostEnrolledLearnings",
    );

    const learnings = _.get(reportsState, "items") || [];
    const loading = _.get(reportsState, "loading");

    return {
      learnings,
      loading,
    };
  }, _.isEqual);

    const handlePaginationClick = useCallback((itemIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const items = container.querySelectorAll<HTMLElement>(
      "[data-carousel-pagination-item='true']"
    );

    const targetItem = items[itemIndex];
    if (!targetItem) return;

    container.scrollTo({
      left: targetItem.offsetLeft,
      behavior: "smooth",
    });
  }, []);

  const syncActiveItemIndex = useCallback((container: HTMLDivElement) => {

    if (!scrollContainerRef.current) {
      scrollContainerRef.current = container;
    }

    const itemElements = Array.from(
      container.querySelectorAll<HTMLElement>("[data-carousel-pagination-item='true']"),
    );

    if (_.isEmpty(itemElements)) {
      setActiveItemIndex(0);
      return;
    }

    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const isAtEnd = Math.abs(scrollLeft - maxScroll) < 2;

    if (isAtEnd) {
      setActiveItemIndex(itemElements.length - 1);
      return;
    }

    const nextActiveIndex = itemElements.findIndex(
      (item) => item.offsetLeft + item.offsetWidth / 2 > scrollLeft + 1,
    );

    setActiveItemIndex(
      nextActiveIndex === -1 ? itemElements.length - 1 : nextActiveIndex,
    );
  }, []);

  useEffect(() => {
    setActiveItemIndex((previous) => {
      if (_.isEmpty(learnings)) {
        return 0;
      }

      return Math.min(previous, learnings.length - 1);
    });
  }, [learnings]);

  const handleClick = (item) => {
    const navigationState = {
      state: buildOriginNavigationState(COACHING_EXPLORE_PATH),
    };

    if (item.type === "COURSE") {
      navigate(`/coaching/explore/courses/${item.course?.id}`, navigationState);
    } else if (item.type === "SIMULATION") {
      navigate(`/coaching/explore/simulations/${item.course?.id}`, navigationState);
    } else if (item.type === "PODCAST") {
      navigate(`/coaching/explore/podcasts/${item.course?.id}`, navigationState);
    }
  };
  
  const getHeader = () => {
    if (loading) {
      return <span className={styles.cardHeading} />;
    }
  
    return (
      <span className={styles.text}>
        learnings popular with your team
      </span>
    );
  };

  if(learnings.length === 0 && !loading ){
    return null;
  }

  return (
    <section className={cx(styles.sectionContainer, styles.popularLearnings)}>
      <header className={styles.title}>
        <div>
          {getHeader()}
        </div>
        <PaginationIndicator
          current={0}
          totalItems={learnings.length}
          activeItemIndex={activeItemIndex}
          groupSize={5}
        />
      </header>

      <div className={styles.learnings}>
        <ScrollableContainer
          distance={300}
          wrapperClassName={styles.courseList}
          leftArrowComponent={LeftArrow}
          rightArrowComponent={RightArrow}
          onScrollPositionChange={syncActiveItemIndex} 
        >

          {(loading && _.size(learnings) === 0 || loading) &&
            _.times(4).map((i) => (
              <div key={i} className={styles.wrapper}>
                <PlaceholderCourseCard />
              </div>
            ))
          }

          {!loading && learnings.map((item: any, index) => (
            <div key={index} className={styles.wrapper} data-carousel-pagination-item="true">
              <div className={styles.number}>{index + 1}</div>
              {item?.course?.entityType === "course" && (
                <CourseCard
                  key={item.id}
                  course={item.course}
                  onClick={() => handleClick(item)}
                />
              )}

              {item?.course?.entityType === "simulation" && (
                <SimulationCard
                  key={item.id}
                  course={item.course}
                  onClick={() => handleClick(item)}
                />
              )}

              {item?.course?.entityType === "podcast" && (
                <PodcastCard
                  key={item.id}
                  course={item.course}
                  onClick={() => handleClick(item)}
                />
              )}
            </div>
          ))}
        </ScrollableContainer>
      </div>
    </section>
  );
};


const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.left)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.right)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const CallCard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state: any) => _.get(state, "users.me"));

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  const { items, loading, loaded, hasMoreItems, nextPage, currentPage, total, totalRecordsCount, callMeetingState } = useSelector((state: any) => {
    const callMeetingState = _.get(state.coaching, "callMeetings", {});
    const items = _.get(callMeetingState, "items", []);
    const loading = _.get(callMeetingState, "loading", false);
    const loaded = _.get(callMeetingState, "loaded", false);
    const pagination = _.get(callMeetingState, "pagination");
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0)
    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return { items, loading, loaded, hasMoreItems, nextPage, currentPage, total, totalRecordsCount, callMeetingState };
  }, _.isEqual);


  const handleLoadMore = () => {
    if (nextPage) {
      const request = {
        page: nextPage,
        limit: 10,
        user: user.id,
      };
      dispatch(fetchCallMeetings(request));
    }
  };

  const PAGE_LIMIT = 10;
  const pendingScrollItemIndexRef = useRef<number | null>(null);

  const fetchCallMeetingsForPage = useCallback((page: number) => {
    dispatch(
      fetchCallMeetings({
        page,
        limit: PAGE_LIMIT,
        user: user.id,
      }),
    );
  }, []);

  const handlePaginationClick = useCallback((itemIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const itemElements = container.querySelectorAll<HTMLElement>(
      "[data-carousel-pagination-item='true']"
    );

    const targetItem = itemElements[itemIndex];

    if (targetItem) {
      container.scrollTo({
        left: targetItem.offsetLeft,
        behavior: "smooth",
      });
      return;
    }

    pendingScrollItemIndexRef.current = itemIndex;

    const requiredPage = Math.floor(itemIndex / PAGE_LIMIT);

    if (requiredPage > currentPage) {
      fetchCallMeetingsForPage(requiredPage);
    }
  }, [currentPage, fetchCallMeetingsForPage]);

  const syncActiveItemIndex = useCallback((container: HTMLDivElement) => {
    
    if (!scrollContainerRef.current) {
      scrollContainerRef.current = container;
    }

    const itemElements = Array.from(
      container.querySelectorAll<HTMLElement>("[data-carousel-pagination-item='true']"),
    );

    if (_.isEmpty(itemElements)) {
      setActiveItemIndex(0);
      return;
    }

    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const isAtEnd = Math.abs(scrollLeft - maxScroll) < 2;

    if (isAtEnd) {
      setActiveItemIndex(itemElements.length - 1);
      return;
    }

    const nextActiveIndex = itemElements.findIndex(
      (item) => item.offsetLeft + item.offsetWidth / 2 > scrollLeft + 1,
    );

    setActiveItemIndex(
      nextActiveIndex === -1 ? itemElements.length - 1 : nextActiveIndex,
    );
  }, []);

  useEffect(() => {
    setActiveItemIndex((previous) => {
      if (_.isEmpty(items)) {
        return 0;
      }

      return Math.min(previous, items.length - 1);
    });
  }, [items]);

  useEffect(() => {

    dispatch(
      fetchCallMeetings({
        page: 0,
        limit: 10,
        user: user.id,
      }),
    );
  }, []);

  useEffect(() => {
    const pendingIndex = pendingScrollItemIndexRef.current;
    const container = scrollContainerRef.current;

    if (pendingIndex === null || !container) return;

    const itemElements = container.querySelectorAll<HTMLElement>(
      "[data-carousel-pagination-item='true']"
    );

    const targetItem = itemElements[pendingIndex];

    if (!targetItem) return;

    container.scrollTo({
      left: targetItem.offsetLeft,
      behavior: "smooth",
    });

    pendingScrollItemIndexRef.current = null;
  }, [items]);

  if (items.length === 0 && !loading) {
    return null;
  }

  const getHeader = () => {
    if (loading) {
      return <span className={styles.cardHeading} />;
    }
  
    return (
      <span className={styles.text}>
        your calls
      </span>
    );
  };

  return (
    <section className={styles.call}>
        <header className={styles.header}>
          <div>
            {getHeader()}
            {/* <span className={styles.action} onClick={() => navigate("/coaching/meetings")}>
              <span className={styles.seeAll}>see all</span>
              <i className="paperflite-up-arrow" />
            </span> */}
          </div>
          <PaginationIndicator
            total={total}
            current={currentPage}
            totalItems={totalRecordsCount}
            activeItemIndex={activeItemIndex}
            groupSize={5}
          />
  
        </header>
        <ScrollableContainer
          distance={300}
          wrapperClassName={styles.courseList}
          leftArrowComponent={LeftArrow}
          rightArrowComponent={RightArrow}
          onScrollPositionChange={syncActiveItemIndex}
        >
      <InfiniteScroll
        className={styles.callList}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMore}
        loader={() => {
          return _.times(
            _.size(items) === 0 || currentPage === 0 ? 3 : 1,
            (index) => <CallCardPlaceHolder />,
          );
        }}
      >
        {items.map((meeting: CallMeeting) => {
          const modifiedDate = _.get(meeting, 'modifiedDate',0);
          const startTime = _.get(meeting,'startTime',0);
          const meetingScore = _.get(meeting, 'score', 0);
          const meetingProspect = _.get(meeting, 'prospect', '');
          const meetingTitle = _.get(meeting, 'title', '');
          const dateMap = new Date(modifiedDate);
          const day = dateMap.getDate();
          const month = dateMap.toLocaleString("default", { month: "short" });
          const time = moment(startTime).isValid()
            ? moment(startTime).format("hh:mm a")
            : "N/A";
          const score = Math.floor((((meetingScore as number) ?? 0) / 5) * 100);
          const prospect = meetingProspect;
          const title = meetingTitle;

          return (
            <div
              key={meeting.id}
              className={styles.callCard}
              onClick={() => navigate(`/coaching/meetings/${meeting.id}`)}
              data-carousel-pagination-item="true"
            >
              <div className={styles.header}>
                <div className={styles.date}>
                  <span className={styles.month}>{month}</span>
                  <span className={styles.day}>{day}</span>
                </div>
                <div className={styles.score}>
                  score : <span>{score}%</span>
                </div>
              </div>

              <div className={styles.meta}>
                <div className={styles.time}>
                  <i className={cx("paperflite-clock",styles.icon)} />
                  {time}
                </div>

                {prospect && <span className={styles.divider} />}

                {prospect && <div className={styles.contact}>
                  <div className={styles.avatar}>
                    {prospect.slice(0, 2).toUpperCase() || "--"}
                  </div>
                  <Text className={styles.contactName} lines={1} text={prospect}/>
                </div>}
              </div>

              <Text className={styles.title} text={title} lines={2} width={100} />

              {/* <div className={styles.deal}> */}
                {/* <i className={cx("paperflite-deal-room",styles.icon)} /> */}
                {/* <span className={styles.dealName}>"deal name will come here"</span> */}
              {/* </div> */}
              
            </div>
          );
        })}
      </InfiniteScroll>
      </ScrollableContainer>
    </section>
  );
};

const CallCardPlaceHolder = () => {
  return (
    <div className={cx(styles.callPlaceholder)}>
      <div className={styles.header}>
        <div className={cx(styles.date, styles.block)}></div>
        <div className={cx(styles.score, styles.block)}></div>
      </div>
      <div className={styles.meta}>
        <div className={cx(styles.data, styles.block)}></div>
        <div className={styles.divider}></div>
        <div className={cx(styles.data, styles.block)}></div>
      </div>
      <div className={cx(styles.title, styles.block)}></div>
      {/* <div className={styles.deal}></div> */}
    </div>
  )
}

export const NoLearningPlaceholder: React.FC = () => {
  return (
    <div className={styles.noLearning}>
      
      <div className={styles.message}>
        <img src={EmptyResult} alt="No learnings found" />
        <div className={styles.dividerline}></div>
        <div className={styles.text}>
          <div className={styles.heading}>No Learnings published yet</div>
          <div className={styles.subHeading}>Published learnings will show up here as soon as they&rsquo;re ready to go live.</div>
        </div>
      </div>
    </div>
  );
};